// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/views/jobs/searches/CloseSearchModal.js
//
// Clôture d'une recherche. Le propriétaire choisit un motif ; un admin qui modère l'annonce d'un
// AUTRE membre n'en choisit pas (le serveur impose « moderation »). Une clôture est définitive :
// l'interface le dit avant de confirmer.

import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CFormCheck,
  CAlert,
  CSpinner,
} from '@coreui/react'
import { closeSearch } from '../../../utils/memberSearchesApi'
import { OWNER_CLOSE_REASONS, parseApiError, errorKeyFor } from '../../../utils/memberSearches'
import './searches.scss'

/**
 * @param {{search: (object|null), moderation?: boolean, onClose: Function, onClosed: Function}} props
 *   search = annonce à clôturer ; null = modale fermée.
 */
const CloseSearchModal = ({ search, moderation = false, onClose, onClosed }) => {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const savingRef = useRef(false)

  useEffect(() => {
    if (search) {
      setReason('')
      setMessage(null)
    }
  }, [search])

  const confirm = async () => {
    if (savingRef.current || (!moderation && !reason)) return
    savingRef.current = true
    setSaving(true)
    setMessage(null)
    try {
      const data = await closeSearch(search.id, moderation ? undefined : reason)
      onClosed(data.search)
      onClose()
    } catch (err) {
      setMessage(t(`searches:${errorKeyFor(parseApiError(err).code)}`))
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  return (
    <CModal
      visible={Boolean(search)}
      onClose={saving ? undefined : onClose}
      alignment="center"
      backdrop="static"
    >
      <CModalHeader closeButton={!saving}>
        <CModalTitle>{t('searches:close.title')}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {search && <p className="fw-semibold mb-2">{search.title}</p>}
        {message && <CAlert color="danger">{message}</CAlert>}

        {moderation ? (
          <p className="mb-2">{t('searches:close.reasons.moderation')}</p>
        ) : (
          <>
            <p className="mb-2">{t('searches:close.reason')}</p>
            {OWNER_CLOSE_REASONS.map((r) => (
              <CFormCheck
                key={r}
                type="radio"
                name="ms-close-reason"
                id={`ms-close-${r}`}
                label={t(`searches:close.reasons.${r}`)}
                checked={reason === r}
                onChange={() => setReason(r)}
              />
            ))}
          </>
        )}
        <p className="ms-note mt-3 mb-0">{t('searches:close.irreversible')}</p>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" disabled={saving} onClick={onClose}>
          {t('searches:actions.cancel')}
        </CButton>
        <CButton color="danger" disabled={saving || (!moderation && !reason)} onClick={confirm}>
          {saving && <CSpinner size="sm" className="me-2" />}
          {t('searches:close.confirm')}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default CloseSearchModal
